import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  alpha,
  useTheme,
  Paper,
  Fade,
  Zoom,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Storage,
  Memory,
  CheckCircle,
  Error as ErrorIcon,
  AccessTime,
  Speed,
  DataObject,
  Key as KeyIcon,
  FiberManualRecord,
  Refresh,
  HelpOutline,
  CloudOff
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

// Friendly descriptions for non-technical users
const SERVICE_INFO = {
  mongodb: {
    name: 'MongoDB',
    what: 'Your main database — stores all your app data',
    color: '#4DB33D',
    darkColor: '#3FA037',
    gradient: 'linear-gradient(135deg, #4DB33D 0%, #3FA037 100%)',
  },
  redis: {
    name: 'Redis',
    what: 'Fast cache & sessions — keeps things speedy',
    color: '#DC382D',
    darkColor: '#A41E11',
    gradient: 'linear-gradient(135deg, #DC382D 0%, #A41E11 100%)',
  }
};

const METRIC_HELP = {
  uptime: 'How long this service has been running without a restart',
  version: 'The installed software version',
  memory: 'How much RAM this service is currently using',
  connections: 'Number of active connections to this service right now',
  databases: 'Total number of databases stored in MongoDB',
  keys: 'Total number of cached items stored in Redis',
};

function MetricRow({ icon, label, value, helpText, color }) {
  const theme = useTheme();
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      py: 1.5,
      px: 2,
      borderRadius: 2,
      background: alpha(theme.palette.action.hover, 0.4),
      '&:hover': {
        background: alpha(theme.palette.action.hover, 0.7),
      },
      transition: 'background 0.2s ease',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {React.cloneElement(icon, { sx: { fontSize: 20, color: color || 'text.secondary' } })}
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
        {helpText && (
          <Tooltip title={helpText} arrow placement="top">
            <HelpOutline sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        )}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', color: 'text.primary' }}>
        {value}
      </Typography>
    </Box>
  );
}

function MemoryBar({ used, max, color, gradient }) {
  const theme = useTheme();
  const percent = max ? Math.min((used / max) * 100, 100) : 0;
  const isHigh = percent > 80;

  return (
    <Box sx={{
      py: 1.5,
      px: 2,
      borderRadius: 2,
      background: alpha(theme.palette.action.hover, 0.4),
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Memory sx={{ fontSize: 20, color: isHigh ? 'warning.main' : 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Memory
          </Typography>
          <Tooltip title={METRIC_HELP.memory} arrow placement="top">
            <HelpOutline sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
          {formatMemory(used)}{max ? ` / ${formatMemory(max)}` : ''}
        </Typography>
      </Box>
      {max > 0 && (
        <Box sx={{ position: 'relative' }}>
          <LinearProgress
            variant="determinate"
            value={percent}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(color, 0.1),
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background: isHigh
                  ? 'linear-gradient(90deg, #F2994A, #EB5757)'
                  : gradient,
              }
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              right: 0,
              top: 12,
              fontWeight: 600,
              fontSize: '0.65rem',
              color: isHigh ? 'warning.main' : 'text.disabled',
            }}
          >
            {percent.toFixed(0)}%
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function formatUptime(seconds) {
  if (!seconds) return 'N/A';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatMemory(bytes) {
  if (!bytes) return 'N/A';
  const mb = bytes / (1024 * 1024);
  if (mb < 1024) return `${mb.toFixed(0)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

function ServiceCard({ service, icon, delay = 0 }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const info = SERVICE_INFO[service];

  useEffect(() => {
    fetchServiceStatus();
    const interval = setInterval(fetchServiceStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchServiceStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/services/${service}`, {
        withCredentials: true
      });
      setServiceStatus(response.data);
    } catch (error) {
      setServiceStatus({ connected: false, error: error.message });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchServiceStatus();
  };

  const isConnected = serviceStatus?.connected === true;

  return (
    <Zoom in timeout={300 + delay}>
      <Box>
        <Paper
          elevation={0}
          sx={{
            background: alpha(theme.palette.background.paper, 0.85),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(
              isConnected ? info.color : theme.palette.error.main,
              isConnected ? 0.2 : 0.15
            )}`,
            borderRadius: 4,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.common.black, 0.08)}`,
            }
          }}
        >
          {/* Status Banner - big and obvious */}
          <Box sx={{
            background: isConnected
              ? `linear-gradient(135deg, ${alpha(info.color, 0.08)} 0%, ${alpha(info.color, 0.03)} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)} 0%, ${alpha(theme.palette.error.light, 0.03)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            p: 2.5,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Service icon */}
                <Box sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isConnected ? info.gradient : alpha(theme.palette.grey[500], 0.2),
                  boxShadow: isConnected
                    ? `0 4px 12px ${alpha(info.color, 0.3)}`
                    : 'none',
                  transition: 'all 0.3s ease',
                }}>
                  {React.cloneElement(icon, { sx: { color: 'white', fontSize: 22 } })}
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {info.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                    {info.what}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Refresh button */}
                <Tooltip title="Check status now" arrow>
                  <IconButton
                    size="small"
                    onClick={handleRefresh}
                    disabled={refreshing}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: alpha(theme.palette.action.hover, 0.5),
                      '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.8) },
                    }}
                  >
                    <Refresh sx={{
                      fontSize: 16,
                      animation: refreshing ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
                    }} />
                  </IconButton>
                </Tooltip>

                {/* Status badge */}
                <Chip
                  icon={
                    loading ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> :
                    isConnected
                      ? <FiberManualRecord sx={{
                          fontSize: '12px !important',
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.4 }
                          }
                        }} />
                      : <ErrorIcon sx={{ fontSize: '14px !important' }} />
                  }
                  label={loading ? 'Checking...' : isConnected ? 'Running' : 'Down'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    height: 28,
                    background: loading
                      ? alpha(theme.palette.grey[500], 0.1)
                      : isConnected
                        ? alpha(info.color, 0.1)
                        : alpha(theme.palette.error.main, 0.1),
                    color: loading
                      ? 'text.secondary'
                      : isConnected
                        ? info.color
                        : theme.palette.error.main,
                    border: `1px solid ${loading
                      ? alpha(theme.palette.grey[500], 0.2)
                      : isConnected
                        ? alpha(info.color, 0.3)
                        : alpha(theme.palette.error.main, 0.3)}`,
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Content area */}
          <Box sx={{ p: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                <CircularProgress size={28} sx={{ color: info.color }} />
              </Box>
            ) : isConnected ? (
              <Fade in timeout={400}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* Version */}
                  {serviceStatus.version && (
                    <MetricRow
                      icon={<Speed />}
                      label="Version"
                      value={serviceStatus.version}
                      helpText={METRIC_HELP.version}
                      color={info.color}
                    />
                  )}

                  {/* Uptime */}
                  {serviceStatus.uptime != null && (
                    <MetricRow
                      icon={<AccessTime />}
                      label="Uptime"
                      value={formatUptime(serviceStatus.uptime)}
                      helpText={METRIC_HELP.uptime}
                    />
                  )}

                  {/* Memory with progress bar */}
                  {serviceStatus.memory != null && (
                    <MemoryBar
                      used={serviceStatus.memory}
                      max={serviceStatus.memoryMax}
                      color={info.color}
                      gradient={info.gradient}
                    />
                  )}

                  {/* Connections */}
                  {serviceStatus.connections != null && (
                    <MetricRow
                      icon={<DataObject />}
                      label="Connections"
                      value={serviceStatus.connections}
                      helpText={METRIC_HELP.connections}
                    />
                  )}

                  {/* Databases (MongoDB only) */}
                  {serviceStatus.databases != null && (
                    <MetricRow
                      icon={<Storage />}
                      label="Databases"
                      value={serviceStatus.databases}
                      helpText={METRIC_HELP.databases}
                    />
                  )}

                  {/* Keys (Redis only) */}
                  {serviceStatus.keys != null && (
                    <MetricRow
                      icon={<KeyIcon />}
                      label="Cached Keys"
                      value={serviceStatus.keys.toLocaleString()}
                      helpText={METRIC_HELP.keys}
                    />
                  )}
                </Box>
              </Fade>
            ) : (
              /* Disconnected state - friendly and helpful */
              <Box sx={{
                py: 3,
                px: 2,
                textAlign: 'center',
                borderRadius: 2,
                background: alpha(theme.palette.error.main, 0.03),
              }}>
                <CloudOff sx={{ fontSize: 36, color: 'text.disabled', mb: 1.5 }} />
                <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
                  {info.name} is not responding
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 280, mx: 'auto' }}>
                  {serviceStatus?.error || `The service might be stopped or not installed on this server.`}
                </Typography>
                <Chip
                  icon={<Refresh sx={{ fontSize: 14 }} />}
                  label="Try Again"
                  size="small"
                  onClick={handleRefresh}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) },
                  }}
                />
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Zoom>
  );
}

export default function ServiceStatus() {
  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} md={6}>
        <ServiceCard
          service="mongodb"
          icon={<Storage />}
          delay={0}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <ServiceCard
          service="redis"
          icon={<Memory />}
          delay={100}
        />
      </Grid>
    </Grid>
  );
}
